import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { EDIT_CLIENT_QUERY, paths } from '@/app/router/paths';
import { Button, Card, Skeleton } from '@/design-system';
import { formatClientAddress, formatContactLine } from '@/entities/client';
import { lifecycleSteps, orderAccessFor, orderApi, orderKeys, type Order } from '@/entities/order';
import { formatFloorArea } from '@/entities/property';
import { useSessionStore } from '@/shared/api';
import { formatDayShort, formatTime } from '@/shared/lib';
import { CardTitle, ErrorState, LifecycleStrip, Screen, ScreenBody, TabHeader } from '@/shared/ui';

import { ReopenSurveyButton } from '@/features/inspections/components/ReopenSurveyButton';

import { ChangeAddressPanel } from './components/ChangeAddressPanel';
import { DocumentsCard } from './components/DocumentsCard';
import { EditClientPanel } from './components/EditClientPanel';
import { FinancialCard } from './components/FinancialCard';
import { ServicesCard } from './components/ServicesCard';
import { TimelineCard } from './components/TimelineCard';
import { dossierCopy } from './copy';

/**
 * Scherm 8 - het tabblad Overzicht van een keuring (vroeger: Dossier): alles
 * van één keuring bij elkaar, op haar uuid.
 *
 * Het staat onder de kop van de keuring (`OrderLayout`). Die zegt wat deze
 * keuring is en opent opname en rapport met haar tabbladen; hier staat alleen
 * wat je op dit tabblad doet.
 *
 * Het financiële blok is alleen voor `owner`. De API laat de bedragen voor een
 * inspecteur weg, en dan is er ook geen leeg kader.
 */
export function DossierPage() {
    const navigate = useNavigate();
    const { orderId = '' } = useParams<{ orderId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const session = useSessionStore((state) => state.session);
    const access = orderAccessFor(session);
    const [clientSaved, setClientSaved] = useState<string | null>(null);
    const [changingAddress, setChangingAddress] = useState(false);
    const [addressSaved, setAddressSaved] = useState<string | null>(null);
    /* `?klant=bewerken` opent "Klant bewerken" meteen: daar wijst het rapport heen zonder e-mailadres. */
    const editingClient =
        access.manage && searchParams.get(EDIT_CLIENT_QUERY.param) === EDIT_CLIENT_QUERY.value;

    const setEditingClient = (editing: boolean) => {
        const next = new URLSearchParams(searchParams);

        if (editing) {
            next.set(EDIT_CLIENT_QUERY.param, EDIT_CLIENT_QUERY.value);
        } else {
            next.delete(EDIT_CLIENT_QUERY.param);
        }

        setSearchParams(next, { replace: true });
    };

    const detail = useQuery({
        queryKey: orderKeys.detail(orderId),
        queryFn: () => orderApi.show(orderId),
        enabled: orderId !== '',
    });

    const timeline = useQuery({
        queryKey: orderKeys.timeline(orderId),
        queryFn: () => orderApi.timeline(orderId),
        enabled: orderId !== '',
    });

    const documents = useQuery({
        queryKey: orderKeys.documents(orderId),
        queryFn: () => orderApi.documents(orderId),
        enabled: orderId !== '',
    });

    const order = detail.data;

    return (
        <Screen>
            <TabHeader
                actions={
                    order ? <ReopenSurveyButton order={order} variant="onDark" size="sm" /> : null
                }
            />

            <ScreenBody>
                {detail.isError ? (
                    <ErrorState
                        error={detail.error}
                        title={dossierCopy.errorTitle}
                        onRetry={() => void detail.refetch()}
                    />
                ) : order === undefined ? (
                    <Skeleton height={320} radius={10} pulse label={dossierCopy.loading} />
                ) : (
                    <>
                        <Card padding="lg" className="mb-[14px]">
                            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-[14px]">
                                <CardTitle>{dossierCopy.lifecycleTitle}</CardTitle>
                                <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                                    {order.status === 'cancelled'
                                        ? dossierCopy.cancelled(order.cancelledReason)
                                        : dossierCopy.lifecycleNote}
                                </span>
                            </div>
                            <LifecycleStrip
                                steps={lifecycleSteps(order)}
                                size="md"
                                label={dossierCopy.lifecycleTitle}
                            />
                        </Card>

                        <div className="grid items-start gap-[14px] [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
                            <div className="flex min-w-0 flex-col gap-[14px]">
                                <ObjectCard
                                    order={order}
                                    saved={addressSaved}
                                    onChangeAddress={
                                        access.changeAddress
                                            ? () => {
                                                  setAddressSaved(null);
                                                  setChangingAddress(true);
                                              }
                                            : undefined
                                    }
                                />

                                {access.changeAddress ? (
                                    <ChangeAddressPanel
                                        order={order}
                                        open={changingAddress}
                                        onClose={() => setChangingAddress(false)}
                                        onSaved={(moved) => {
                                            setChangingAddress(false);
                                            setAddressSaved(
                                                dossierCopy.changeAddress.saved(
                                                    moved.property.fullAddress,
                                                ),
                                            );
                                        }}
                                    />
                                ) : null}

                                <ServicesCard order={order} />

                                {timeline.isError ? (
                                    <ErrorState
                                        error={timeline.error}
                                        title={dossierCopy.timelineError}
                                        onRetry={() => void timeline.refetch()}
                                    />
                                ) : (
                                    <TimelineCard
                                        entries={timeline.data ?? []}
                                        loading={timeline.isPending}
                                    />
                                )}

                                <DocumentsCard
                                    documents={documents.data ?? []}
                                    loading={documents.isPending}
                                    error={documents.isError ? documents.error : null}
                                    onRetry={() => void documents.refetch()}
                                />
                            </div>

                            <div className="flex min-w-0 flex-col gap-[14px]">
                                <Card padding="md">
                                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                        <CardTitle>{dossierCopy.clientTitle}</CardTitle>
                                        {access.manage ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                icon="edit"
                                                onClick={() => {
                                                    setClientSaved(null);
                                                    setEditingClient(true);
                                                }}
                                            >
                                                {dossierCopy.editClient.button}
                                            </Button>
                                        ) : null}
                                    </div>
                                    {clientSaved ? (
                                        <p
                                            role="status"
                                            className="m-0 mb-2 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                                        >
                                            {clientSaved}
                                        </p>
                                    ) : null}
                                    <div className="text-[14px] font-medium text-[var(--text-primary)]">
                                        {order.client.name}
                                    </div>
                                    <div className="mt-1 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                                        {formatContactLine(order.client)}
                                        {formatClientAddress(order.client.address) ? (
                                            <>
                                                <br />
                                                {formatClientAddress(order.client.address)}
                                            </>
                                        ) : null}
                                        {order.client.email ? (
                                            <>
                                                <br />
                                                <a
                                                    href={`mailto:${order.client.email}`}
                                                    className="text-[var(--accent)] underline-offset-2 hover:underline"
                                                >
                                                    {order.client.email}
                                                </a>
                                            </>
                                        ) : null}
                                    </div>
                                    {order.notes ? (
                                        <>
                                            <div className="my-[14px] h-px bg-[var(--border-subtle)]" />
                                            <div className="mb-[5px] text-[12px] text-[var(--text-tertiary-safe)]">
                                                {dossierCopy.notesTitle}
                                            </div>
                                            <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                                                {order.notes}
                                            </p>
                                        </>
                                    ) : null}
                                </Card>

                                {access.manage ? (
                                    <EditClientPanel
                                        client={order.client}
                                        open={editingClient}
                                        onClose={() => setEditingClient(false)}
                                        onSaved={(saved) => {
                                            setEditingClient(false);
                                            setClientSaved(
                                                dossierCopy.editClient.saved(saved.name),
                                            );
                                        }}
                                    />
                                ) : null}

                                {order.financials ? (
                                    <FinancialCard
                                        financials={order.financials}
                                        serviceLabel={order.serviceLabel}
                                        onToBilling={
                                            access.invoices
                                                ? () => navigate(paths.billing)
                                                : undefined
                                        }
                                    />
                                ) : null}
                            </div>
                        </div>
                    </>
                )}
            </ScreenBody>
        </Screen>
    );
}

/**
 * Objectgegevens in vier kolommen: bouwjaar, type, oppervlak en opnamemoment. Met
 * `onChangeAddress` (wie keuringen beheert) eronder "Adres van het object wijzigen";
 * dicht met uitleg zodra het rapport bij de klant is.
 */
function ObjectCard({
    order,
    saved,
    onChangeAddress,
}: {
    order: Order;
    saved: string | null;
    onChangeAddress?: () => void;
}) {
    const property = order.property;

    const pairs: Array<{ label: string; value: string }> = [
        {
            label: dossierCopy.buildYear,
            value: property.buildYear ? String(property.buildYear) : dossierCopy.unknown,
        },
        {
            label: dossierCopy.propertyType,
            value: property.propertyTypeLabel ?? dossierCopy.unknown,
        },
        {
            label: dossierCopy.floorArea,
            value: formatFloorArea(property.floorArea),
        },
        {
            label: dossierCopy.inspection,
            value: order.scheduledAt
                ? `${formatDayShort(order.scheduledAt)} · ${formatTime(order.scheduledAt)}`
                : dossierCopy.notPlanned,
        },
    ];

    return (
        <Card padding="lg">
            <dl className="m-0 grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(112px,1fr))]">
                {pairs.map((pair) => (
                    <div key={pair.label}>
                        <dt className="mb-[5px] text-[12px] text-[var(--text-tertiary-safe)]">
                            {pair.label}
                        </dt>
                        <dd className="m-0 text-[15px] font-medium text-[var(--text-primary)]">
                            {pair.value}
                        </dd>
                    </div>
                ))}
            </dl>
            <p className="m-0 mt-3 text-[12px] text-[var(--text-tertiary-safe)]">
                {property.fullAddress}
                {order.assignee ? ` · ${dossierCopy.inspector(order.assignee.name)}` : ''}
            </p>
            {saved ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {saved}
                </p>
            ) : null}
            {onChangeAddress ? (
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                    <Button
                        variant="outline"
                        size="sm"
                        icon="edit"
                        disabled={order.addressLocked}
                        onClick={onChangeAddress}
                    >
                        {dossierCopy.changeAddress.button}
                    </Button>
                    {order.addressLocked ? (
                        <span className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                            {dossierCopy.changeAddress.locked}
                        </span>
                    ) : null}
                </div>
            ) : null}
        </Card>
    );
}
